import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { hasPermission } from "../../utils/permissions";
import type { ApAgingReport, ArAgingReport, DashboardReport, FinanceDepartment } from "../../types";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const toDateString = (d: Dayjs) => d.format("YYYY-MM-DD");

/** Trend node — omitted entirely when the comparison base is missing. */
function Trend({ value, invert = false }: { value: number | null; invert?: boolean }) {
  if (value === null || !Number.isFinite(value)) return null;
  const good = invert ? value < 0 : value > 0;
  const color = value === 0 ? "#8c8c8c" : good ? "#52c41a" : "#a5171f";
  return (
    <Text type="secondary" style={{ fontSize: 12, color }}>
      {value > 0 ? "▲" : value < 0 ? "▼" : "•"} {Math.abs(value).toFixed(1)}% vs previous period
    </Text>
  );
}

const FinanceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(29, "day"), dayjs()]);
  const [departmentId, setDepartmentId] = useState<string | undefined>(undefined);
  const [data, setData] = useState<DashboardReport | null>(null);
  const [departments, setDepartments] = useState<FinanceDepartment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Who owes what at the end of the selected range. Kept separate from
   * `data` because it is a point-in-time report rather than a window: it is
   * struck at `range[1]`, not accumulated across the range.
   *
   * `null` means "not loaded or could not be built", and the whole row below
   * is then omitted. It does not mean "nothing is owed" — that distinction
   * is the difference between a dashboard that admits what it does not know
   * and one that prints LKR 0 of nothing owing.
   */
  const [ageing, setAgeing] = useState<{ ap: ApAgingReport; ar: ArAgingReport } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeService.getDashboard({
        from: toDateString(range[0]),
        to: toDateString(range[1]),
        department_id: departmentId,
      });
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load the finance dashboard");
    } finally {
      setLoading(false);
    }
  }, [range, departmentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    financeService
      .getDepartments()
      .then(setDepartments)
      .catch(() => setDepartments([]));
  }, []);

  // The ageings are struck at the END of the selected range, so only that
  // end date is a dependency: dragging the start of the range must not
  // refetch a report that never looked at it. Hoisted into a plain string
  // so the hook dependency can be checked statically.
  const ageingAsOf = toDateString(range[1]);

  useEffect(() => {
    let cancelled = false;
    // The rest of the dashboard answers "what happened in these dates";
    // these two answer "who owes what at the last of them".
    Promise.all([
      financeService.getApAging({ asOf: ageingAsOf }),
      financeService.getArAging({ asOf: ageingAsOf }),
    ])
      .then(([ap, ar]) => {
        if (!cancelled) setAgeing({ ap, ar });
      })
      .catch(() => {
        // A failed ageing says nothing about the resort, so it says nothing
        // on the page. The rest of the dashboard still loads above it.
        if (!cancelled) setAgeing(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ageingAsOf]);

  const trend = useMemo(() => {
    if (!data) return [];
    return data.daily.map((d) => ({
      ...d,
      label: dayjs(d.business_date).format("MMM D"),
    }));
  }, [data]);

  const departmentColumns = [
    {
      title: "Department",
      dataIndex: "name",
      key: "name",
      render: (name: string, row: any) => (
        <Space size={6}>
          <span>{name}</span>
          <Tag>{row.code}</Tag>
        </Space>
      ),
    },
    {
      title: "Income",
      dataIndex: "income",
      key: "income",
      align: "right" as const,
      render: (v: number) => formatLkr(v),
    },
    {
      title: "Expense",
      dataIndex: "expense",
      key: "expense",
      align: "right" as const,
      render: (v: number) => formatLkr(v),
    },
    {
      title: "Profit",
      dataIndex: "profit",
      key: "profit",
      align: "right" as const,
      render: (v: number) => (
        <Text style={{ color: v >= 0 ? "#52c41a" : "#a5171f" }}>{formatLkr(v)}</Text>
      ),
    },
    {
      title: "Margin",
      dataIndex: "margin",
      key: "margin",
      align: "right" as const,
      // A margin of 0% on zero income is undefined — show nothing rather
      // than a misleading 0%.
      render: (v: number | null) => (v === null ? "—" : `${v.toFixed(1)}%`),
    },
  ];

  const cashColumns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 80,
      render: (value: string) => (
        <Tag color={value === "cash" ? "green" : value === "card" ? "purple" : "blue"}>
          {value}
        </Tag>
      ),
    },
    { title: "Account", dataIndex: "name", key: "name" },
    { title: "GL code", dataIndex: "account_code", key: "account_code", width: 100 },
    {
      title: "Opening",
      dataIndex: "opening_balance",
      key: "opening_balance",
      align: "right" as const,
      render: (v: number) => formatLkr(v),
    },
    {
      title: "Movement",
      dataIndex: "movement",
      key: "movement",
      align: "right" as const,
      render: (v: number) => (
        <Text style={{ color: v >= 0 ? "#52c41a" : "#a5171f" }}>{formatLkr(v)}</Text>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right" as const,
      render: (v: number) => <Text strong>{formatLkr(v)}</Text>,
    },
  ];

  const hasActivity = !!data && (data.income !== 0 || data.expense !== 0);
  const totalCash = data ? data.cash.reduce((s, c) => s + Number(c.balance || 0), 0) : 0;
  const kpis = data?.room_kpis;
  const rkpis = data?.restaurant_kpis;
  const food = data?.food_cost_kpis;
  const owed = data?.supplier_outstanding;
  const payroll = data?.payroll_kpis;

  return (
    <div style={{ padding: 24 }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
        wrap
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Income &amp; Expense
          </Title>
          <Text type="secondary">
            Departmental performance from the general ledger ·{" "}
            {range[0].format("MMM D, YYYY")} – {range[1].format("MMM D, YYYY")}
          </Text>
        </div>
        <Space wrap>
          <Select
            allowClear
            placeholder="All departments"
            style={{ width: 200 }}
            value={departmentId}
            onChange={setDepartmentId}
            options={departments.map((d) => ({ label: d.name, value: d.id }))}
          />
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) setRange([dates[0], dates[1]]);
            }}
          />
        </Space>
      </Space>

      {error && (
        <Alert type="error" title="Failed to load" description={error} showIcon style={{ marginBottom: 16 }} />
      )}

      {data && data.closing.pending_dates.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={`${data.closing.pending_dates.length} business date(s) not yet closed`}
          description={
            <span>
              {data.closing.pending_dates.slice(0, 8).join(", ")}
              {hasPermission("finance:close") && (
                <>
                  {" — "}
                  <a onClick={() => navigate("/finance/close")}>run daily close</a>
                </>
              )}
            </span>
          }
        />
      )}

      {data && (data.pending_postings.pending > 0 || data.pending_postings.failed > 0) && (
        <Alert
          type={data.pending_postings.failed > 0 ? "error" : "warning"}
          showIcon
          style={{ marginBottom: 16 }}
          title={`${data.pending_postings.pending} posting(s) waiting, ${data.pending_postings.failed} failed`}
          description={
            <span>
              {data.pending_postings.pending > 0
                ? "Source-system entries landed while a business date was closed. They post automatically once the date is open — the desk and the cashier are not blocked."
                : "Some payments use a tender that has no cash / bank / card account mapping yet, so their ledger leg is held rather than guessed."}
              {" "}
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: "auto" }}
                onClick={() => navigate("/reports")}
              >
                source reconciliation report
              </Button>
            </span>
          }
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Income"
              value={data?.income ?? 0}
              precision={2}
              suffix="LKR"
              valueStyle={{ color: "#52c41a" }}
            />
            <Trend value={data?.vs_previous.income_change_pct ?? null} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Expense"
              value={data?.expense ?? 0}
              precision={2}
              suffix="LKR"
              valueStyle={{ color: "#a5171f" }}
            />
            <Trend value={data?.vs_previous.expense_change_pct ?? null} invert />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Net Profit"
              value={data?.profit ?? 0}
              precision={2}
              suffix="LKR"
              valueStyle={{ color: (data?.profit ?? 0) >= 0 ? "#b79a68" : "#a5171f" }}
            />
            {/* Margin is undefined with no income — omit instead of showing 0%. */}
            {data && data.margin !== null && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {data.margin.toFixed(1)}% margin
              </Text>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic title="Cash &amp; bank" value={totalCash} precision={2} suffix="LKR" />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {data ? `${data.cash.length} account(s)` : ""}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* ----------------------------------------------------------
          Who owes what, as at the end of the selected range.

          "Overdue" only means something against a stated due date, and
          nothing in the schema stores one — the due dates here are built
          from the payment terms echoed above the cards, so the reader is
          never asked to trust an unexplained definition. The row is absent
          entirely if the ageings could not be built; printing LKR 0 there
          would be the one number on this page invented rather than measured.
         ---------------------------------------------------------- */}
      {ageing && (
        <>
          <Text
            type="secondary"
            style={{ display: "block", marginBottom: 8, fontSize: 12 }}
          >
            Owed at {dayjs(range[1]).format("MMM D, YYYY")} · due {ageing.ap.terms_days} days
            after invoice or check-out
          </Text>

          {!ageing.ap.control.matched || !ageing.ar.control.matched ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Ageing does not reconcile with the ledger"
              description={[
                !ageing.ap.control.matched
                  ? `Payables: listed ${formatLkr(ageing.ap.control.aged_total)} against GL 2010 ${formatLkr(
                      ageing.ap.control.gl_balance,
                    )}.`
                  : null,
                !ageing.ar.control.matched
                  ? `Receivables: expected ${formatLkr(ageing.ar.control.expected_in_gl)} in GL 1100 against ${formatLkr(
                      ageing.ar.control.gl_balance,
                    )}.`
                  : null,
              ]
                .filter(Boolean)
                .join(" ")}
            />
          ) : null}

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Payables overdue"
                  value={ageing.ap.summary.overdue}
                  precision={2}
                  suffix="LKR"
                  valueStyle={{ color: ageing.ap.summary.overdue > 0 ? "#a5171f" : undefined }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {ageing.ap.summary.overdue_count} of {ageing.ap.summary.count} open
                  {ageing.ap.summary.oldest_days_overdue !== null
                    ? ` · oldest ${ageing.ap.summary.oldest_days_overdue}d late`
                    : ""}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Payables open"
                  value={ageing.ap.summary.total}
                  precision={2}
                  suffix="LKR"
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {ageing.ap.summary.overdue > 0
                    ? `${formatLkr(ageing.ap.summary.current)} not yet due`
                    : "nothing past due"}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Receivables overdue"
                  value={ageing.ar.summary.overdue}
                  precision={2}
                  suffix="LKR"
                  valueStyle={{ color: ageing.ar.summary.overdue > 0 ? "#a5171f" : undefined }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {ageing.ar.summary.overdue_count} of {ageing.ar.summary.count} debtor(s)
                  {ageing.ar.summary.oldest_days_overdue !== null
                    ? ` · oldest ${ageing.ar.summary.oldest_days_overdue}d late`
                    : ""}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Receivables open"
                  value={ageing.ar.summary.total}
                  precision={2}
                  suffix="LKR"
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {ageing.ar.summary.guests} guest / {ageing.ar.summary.customers} customer row(s)
                  {ageing.ar.control.pre_cutover > 0
                    ? ` · ${formatLkr(ageing.ar.control.pre_cutover)} predates the cutover`
                    : ""}
                </Text>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* ----------------------------------------------------------
          Room performance. Every figure is derived from the ledger and the
          folio — nothing here is stored. A rate with a zero denominator is
          omitted rather than shown as a misleading 0.
         ---------------------------------------------------------- */}
      {kpis && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Occupancy"
                value={kpis.occupancy_pct === null ? "—" : kpis.occupancy_pct}
                precision={kpis.occupancy_pct === null ? undefined : 1}
                suffix={kpis.occupancy_pct === null ? undefined : "%"}
                valueStyle={kpis.occupancy_pct === null ? { color: "#8c8c8c" } : undefined}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {kpis.rooms_available === null
                  ? "no room inventory configured"
                  : `${kpis.rooms_sold} of ${kpis.rooms_available} room-nights sold`}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="ADR"
                value={kpis.adr === null ? "—" : kpis.adr}
                precision={kpis.adr === null ? undefined : 2}
                suffix={kpis.adr === null ? undefined : "LKR"}
                valueStyle={kpis.adr === null ? { color: "#8c8c8c" } : undefined}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {kpis.adr === null
                  ? "no room nights sold in this range"
                  : `average daily rate over ${kpis.rooms_sold} night(s)`}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="RevPAR"
                value={kpis.revpar === null ? "—" : kpis.revpar}
                precision={kpis.revpar === null ? undefined : 2}
                suffix={kpis.revpar === null ? undefined : "LKR"}
                valueStyle={kpis.revpar === null ? { color: "#8c8c8c" } : undefined}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {kpis.revpar === null
                  ? "no room inventory configured"
                  : "room revenue across all available nights"}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic title="Room revenue" value={kpis.room_revenue} precision={2} suffix="LKR" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {kpis.clamped_from_cutover
                  ? `ledger starts ${kpis.from}`
                  : `${kpis.rooms_sold} night(s) sold`}
              </Text>
            </Card>
          </Col>
        </Row>
      )}

      {/* ----------------------------------------------------------
          Restaurant / KOT performance — the second funnel, and the one that
          recognises differently: a guest charge is booked when it is made, a
          walk-in order when it is settled. A rate with a zero denominator is
          omitted rather than shown as a misleading 0.
         ---------------------------------------------------------- */}
      {rkpis && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic title="Restaurant orders" value={rkpis.orders} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {rkpis.clamped_from_cutover
                  ? `ledger starts ${rkpis.reporting_start}`
                  : `${rkpis.walk_in_orders} walk-in, ${rkpis.orders - rkpis.walk_in_orders} billed to a folio`}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Average ticket"
                value={rkpis.avg_ticket === null ? "—" : rkpis.avg_ticket}
                precision={rkpis.avg_ticket === null ? undefined : 2}
                suffix={rkpis.avg_ticket === null ? undefined : "LKR"}
                valueStyle={rkpis.avg_ticket === null ? { color: "#8c8c8c" } : undefined}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {rkpis.avg_ticket === null
                  ? "no orders settled in this range"
                  : "net of VAT, both funnels"}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Walk-in share"
                value={rkpis.walk_in_share_pct === null ? "—" : rkpis.walk_in_share_pct}
                precision={rkpis.walk_in_share_pct === null ? undefined : 1}
                suffix={rkpis.walk_in_share_pct === null ? undefined : "%"}
                valueStyle={rkpis.walk_in_share_pct === null ? { color: "#8c8c8c" } : undefined}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {rkpis.walk_in_share_pct === null
                  ? "no orders settled in this range"
                  : "settled without a booking"}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic title="Restaurant revenue" value={rkpis.net_revenue} precision={2} suffix="LKR" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {rkpis.channels.length === 0
                  ? "no restaurant activity yet"
                  : rkpis.channels.map((c) => c.label).join(" · ")}
              </Text>
            </Card>
          </Col>
        </Row>
      )}

      {/* ----------------------------------------------------------
          What the food actually cost, and what is still owed for it. Both
          rates are omitted — shown as a dash — when their denominator is
          zero, and the coverage figure says how much of the cost the system
          was able to know at all: a menu item with no recipe posts no issue,
          which would otherwise make the percentage look excellent.
         ---------------------------------------------------------- */}
      {food && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Food Cost %"
                value={food.food_cost_pct === null ? "—" : food.food_cost_pct}
                precision={food.food_cost_pct === null ? undefined : 1}
                suffix={food.food_cost_pct === null ? undefined : "%"}
                valueStyle={food.food_cost_pct === null ? { color: "#8c8c8c" } : undefined}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {food.food_cost_pct === null
                  ? "no restaurant revenue in this window"
                  : `${formatLkr(food.food_cost)} of ingredients against ${formatLkr(
                      food.restaurant_revenue,
                    )} of revenue`}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Recipe coverage"
                value={food.recipe_coverage_pct === null ? "—" : food.recipe_coverage_pct}
                precision={food.recipe_coverage_pct === null ? undefined : 1}
                suffix={food.recipe_coverage_pct === null ? undefined : "%"}
                valueStyle={food.recipe_coverage_pct === null ? { color: "#8c8c8c" } : undefined}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {food.recipe_coverage_pct === null
                  ? "nothing ordered in this window"
                  : `${food.configured_quantity} of ${food.ordered_quantity} ordered units have a recipe`}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Food & beverage cost"
                value={food.food_cost + food.beverage_cost}
                precision={2}
                suffix="LKR"
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {food.clamped_from_cutover
                  ? `ledger starts ${food.reporting_start}`
                  : `${food.from} – ${food.to}`}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Owed to suppliers"
                value={owed ? owed.amount : "—"}
                precision={owed ? 2 : undefined}
                suffix={owed ? "LKR" : undefined}
                valueStyle={owed ? undefined : { color: "#8c8c8c" }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {owed
                  ? `${owed.count} invoice(s) unpaid, oldest ${owed.oldest_business_date}`
                  : "no approved credit purchase outstanding"}
              </Text>
            </Card>
          </Col>
        </Row>
      )}

      {/* ----------------------------------------------------------
          Labour. Aggregate on purpose: `finance:view` is enough to know
          what the resort spends on its people, and `payroll:view` is
          required to know what it pays any individual. Every rate here
          is null — not 0 — when its denominator is zero.
         ---------------------------------------------------------- */}
      {payroll && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic title="Labour cost" value={payroll.labour_cost} precision={2} suffix="LKR" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {payroll.by_department.length
                  ? `${payroll.by_department.length} department(s) carrying cost`
                  : "6100 Salaries & Wages"}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Labour % of income"
                value={payroll.labour_pct_of_income === null ? "—" : payroll.labour_pct_of_income}
                precision={payroll.labour_pct_of_income === null ? undefined : 1}
                suffix={payroll.labour_pct_of_income === null ? undefined : "%"}
                valueStyle={
                  payroll.labour_pct_of_income === null ? { color: "#8c8c8c" } : undefined
                }
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {payroll.labour_pct_of_income === null
                  ? "no income in this window"
                  : `against ${formatLkr(data!.income)} of income`}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Vs previous period"
                value={
                  payroll.labour_change_pct === null ? "—" : payroll.labour_change_pct
                }
                precision={payroll.labour_change_pct === null ? undefined : 1}
                suffix={payroll.labour_change_pct === null ? undefined : "%"}
                valueStyle={
                  payroll.labour_change_pct === null
                    ? { color: "#8c8c8c" }
                    : { color: payroll.labour_change > 0 ? "#cf1322" : "#389e0d" }
                }
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {payroll.labour_change_pct === null
                  ? "nothing posted in the previous period"
                  : `${payroll.runs ?? 0} run(s) · ${
                      payroll.employees_paid === null ? "employee count unknown" : `${payroll.employees_paid} employee(s) paid`
                    }`}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={loading}>
              <Statistic
                title="Still owed to the boards"
                value={payroll.statutory_payable}
                precision={2}
                suffix="LKR"
                valueStyle={payroll.statutory_payable > 0 ? { color: "#d46b08" } : undefined}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                EPF/ETF and APIT withheld but not yet remitted
                {hasPermission("payroll:view") && (
                  <>
                    {" — "}
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: "auto" }}
                      onClick={() => navigate("/finance/payroll")}
                    >
                      payroll
                    </Button>
                  </>
                )}
              </Text>
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Daily income vs expense" size="small" style={{ marginBottom: 16 }}>
            {trend.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No ledger activity in this range"
              />
            ) : (
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" fontSize={11} />
                    <YAxis fontSize={11} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: any) => formatLkr(Number(v))} />
                    <Legend />
                    <Bar dataKey="income" name="Income" fill="#52c41a" maxBarSize={16} />
                    <Bar dataKey="expense" name="Expense" fill="#a5171f" maxBarSize={16} />
                    <Line
                      type="monotone"
                      dataKey="profit"
                      name="Profit"
                      stroke="#b79a68"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card title="Departmental P&amp;L" size="small">
            <Table
              columns={departmentColumns}
              dataSource={data?.by_department ?? []}
              rowKey="department_id"
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ x: 560 }}
              locale={{
                emptyText: (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No departmental activity yet" />
                ),
              }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title="Cash, bank & card position"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Button size="small" onClick={() => navigate("/finance/banking")}>
                Deposits & settlements
              </Button>
            }
          >
            <Table
              columns={cashColumns}
              dataSource={data?.cash ?? []}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ x: 480 }}
              locale={{
                emptyText: (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No cash or bank accounts" />
                ),
              }}
            />
          </Card>

          <Card title="Busiest days" size="small">
            {hasActivity && data ? (
              <Table
                columns={[
                  { title: "Date", dataIndex: "business_date", key: "d" },
                  {
                    title: "Income",
                    dataIndex: "income",
                    align: "right" as const,
                    render: (v: number) => formatLkr(v),
                  },
                  {
                    title: "Expense",
                    dataIndex: "expense",
                    align: "right" as const,
                    render: (v: number) => formatLkr(v),
                  },
                  {
                    title: "Profit",
                    dataIndex: "profit",
                    align: "right" as const,
                    render: (v: number) => (
                      <Text style={{ color: v >= 0 ? "#52c41a" : "#a5171f" }}>{formatLkr(v)}</Text>
                    ),
                  },
                  {
                    title: "",
                    dataIndex: "closed",
                    key: "closed",
                    render: (closed: boolean) =>
                      closed ? <Tag color="green">closed</Tag> : <Tag>open</Tag>,
                  },
                ]}
                dataSource={[...data.daily].sort((a, b) => b.income - a.income).slice(0, 8)}
                rowKey="business_date"
                pagination={false}
                size="small"
                scroll={{ x: 460 }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing posted in this range" />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FinanceDashboard;
